import {autobind} from 'core-decorators';

import {Component} from 'inferno'; // eslint-disable-line @typescript-eslint/no-unused-vars

import {Link} from '../../common/components';
import {OfficialBadge} from '../components/OfficialBadge';
import {BaseSearchUIValue, BaseSearchBar} from '../../common/components/BaseSearchBar';
import {localURL, tryDecodeURI, window} from '../../common/util';
import type {ResponseAndBody} from '../../common/services/apiService';

import './SearchBar.scss';


export class SearchBarValue implements BaseSearchUIValue {
    name: string;
    path: string;
    image?: string;
    userflags?: string[];

    constructor(name, path, image?, userflags?) {
        this.name = name;
        this.path = path;
        this.image = image;
        this.userflags = userflags;
    }
    key() {
        return this.name;
    }
    toUserString() {
        return this.name;
    }
}

interface SuggestionLinkProps<T extends SearchBarValue> {
    idx: number;
    suggestion: T;
    selectedSuggestionIndex: number;
    handleSearchPerformed: Function;
}

function SuggestionLink<
    T extends SearchBarValue>({idx, suggestion, handleSearchPerformed,
    selectedSuggestionIndex}: SuggestionLinkProps<T>) {
    function handleMouseDown() {
        handleSearchPerformed(idx);
    }
    const img = suggestion.image && <img
        className="partner-logo" src={suggestion.image}
    />;
    const name = suggestion.name;

    return (
        <Link
            className={idx === selectedSuggestionIndex ? 'selected' : ''}
            to={suggestion.path}
            onMouseDown={handleMouseDown}
        >
            { img }
            { name }
            { <OfficialBadge flags={suggestion.userflags}/> }
        </Link>);
}

export class SearchBar extends BaseSearchBar<SearchBarValue> {
    routerUnlisten: Function|undefined;

    constructor(props: jsx.Props, context: jsx.AppGlobals) {
        super(props, context);
        this.handleLocationSet();
    }
    componentDidMount() {
        this.routerUnlisten = this.context.router.history.listen(
            this.handleLocationSet);
    }
    componentWillUnmount() {
        this.routerUnlisten?.();
    }
    @autobind
    handleLocationSet() {
        // update input, but only if user isn't currently typing.
        this.setState({
            value: this.pathToString(
                tryDecodeURI(this.context.router.history.location.pathname)),
        });
    }
    @autobind
    placeholder(): string {
        const gettextSub = this.context.gtService.gettextSub;

        // Translators: This is a text input placeholder for the main GIF search
        // Translators: bar.
        return gettextSub('Search for GIFs and Stickers');
    }
    pathToString(path: string): string {
        const delocalizeUrlPath = this.context.gtService.delocalizeUrlPath;

        let s = '';
        path = delocalizeUrlPath(path);
        if (path.substr(0, 8) === '/search/') {
            s = path.substr(8).replace(/(?:-gifs|-stickers|-memes)$/g, '').replace(/-/g, ' ');
        }
        return s;
    }
    stringToPath(s: string): string {
        return this.linkToSearch(s.trim());
    }
    stringToValue(s: string): SearchBarValue {
        return new SearchBarValue(s, this.stringToPath(s));
    }
    fetchSuggestions(reqValue: string): Promise<SearchBarValue[]> {
        const localizeUrlPath = this.context.gtService.localizeUrlPath;

        return this.context.apiService.getAutocompleteResults(reqValue, 3)
            .then(({body}: ResponseAndBody<api.TagListBody>) => {
                return body;
            })
            .then((autocompleteResults: api.TagListBody) => {
                const suggestions: SearchBarValue[] = [];
                if (reqValue === this.state.value.trim()) {
                    let item;
                    const partnerResults =
                        autocompleteResults.profile_autocomplete_results || [];
                    for (item of partnerResults) {
                        const {partnername, url, avatars, flags} = item.user;
                        suggestions.push(new SearchBarValue(
                            partnername,
                            localizeUrlPath(localURL(url)),
                            avatars['32'],
                            flags,
                        ));
                    }
                    for (item of (autocompleteResults.results || [])) {
                        suggestions.push(this.stringToValue(item));
                    }
                    this.setSuggestions(suggestions);
                }
                return suggestions;
            });
    }
    @autobind
    handleSearchPerformed(val: string|number) {
        window.fbq('track', 'Search', {search_string: val});
    }
    renderSuggestionDropDown() {
        const suggestionEls = [];
        const suggestions = this.state.suggestions
            .slice(0, this.maxSuggestions);
        for (const [idx, suggestion] of suggestions.entries()) {
            suggestionEls.push(<SuggestionLink<SearchBarValue>
                key={suggestion.key()}
                idx={idx}
                suggestion={suggestion}
                handleSearchPerformed={this.handleSearchPerformed}
                selectedSuggestionIndex={this.state.selectedSuggestionIndex}
            />);
        }
        return <div className="suggestions-dropdown">
            { suggestionEls }
        </div>;
    }
    @autobind
    handleSubmit(event?: Event) {
        event?.preventDefault();
        this.input?.blur();
        let val: SearchBarValue;
        if (this.state.selectedSuggestionIndex === -1) {
            val = this.stringToValue(this.state.value);
        } else {
            val = this.state.suggestions[this.state.selectedSuggestionIndex];
        }
        this.redirect(val.path);
        this.handleSearchPerformed(val.name.trim());
    }
}
